<template>
  <div>
    <section class="columns">
      <aside class="column is-2">
        <aside class="menu">
          <p class="menu-label">
            Chapters
          </p>
          <ul class="label">
            <li><a v-on:click="this.goToTitle('energyDistribution')">{{ $t("i18n.chartTitles.ccDistribution") }}</a></li>
            <li><a v-on:click="this.goToTitle('usedGenerated')">{{ $t("i18n.chartTitles.ccUsedAndGenerated") }}</a></li>
            <li><a v-on:click="this.goToTitle('usageHour')">{{ $t("i18n.chartTitles.ccEnergyPerTime") }}</a></li>
            <li><a v-on:click="this.goToTitle('temperatureOccurrence')">{{ $t("i18n.chartTitles.ccTemperatureOccurrence") }}</a></li>
            <li><a v-on:click="this.goToTitle('energyPerDegree')">{{ $t("i18n.chartTitles.ccEnergyPerTemperature") }}</a></li>
            <li><a v-on:click="this.goToTitle('averageCop')">{{ $t("i18n.chartTitles.ccAverageCOP") }}</a></li>
            <li><a v-on:click="this.goToTitle('copPerDegree')">{{ $t("i18n.chartTitles.ccCOPPerTemperature") }}</a></li>
          </ul>
        </aside>
      </aside>
      <!--end sidenav start main cont.-->
      <div class="column is-8">
        <div class="block content">
          <h2 class="title is-2">ClimaCheck Energy Reports</h2>
          <p>The ClimaCheck Energy Report gives a comprehensive status of the energy consumption of a system during a quarter.</p>
          <p>The report is automatically sent to the assigned ClimaCheck user at the end of the period and presents key graphs in a directly printable format to in one view give equipment owner an understanding of the period’s energy use.</p>
        </div>
        <div class="block content">
          <h3 class="title is-3" id="navigation">Features</h3>
          <p>The energy report has the following functionallities:</p>
          <h4 class="title is-4">{{ $t("i18n.global.buttons.generateReport") }}</h4>
          <p>Generates a report optimised for a standing A4-paper. Each graph is also exportable via the tripple-dot menu in the graph.</p>
          <h4 class="title is-4">{{ $t("i18n.global.buttons.openManual") }}</h4>
          <p>Opens this document.</p>
          <h4 class="title is-4">{{ $t("i18n.global.buttons.toggleAdvancedInfo") }}</h4>
          <p>Shows and hides information regarding the current system. This information is based on what is known and recorded in ClimaCheck Online.</p>
          <h4 class="title is-4">Language</h4>
          <p>Selection of language.</p>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="energyDistribution">Electrical Energy Distribution</h3>
          <p>The electrical energy distribution graph presents the relative energy consumption of the compressor and the auxiliary loads such as pumps and fans. The graph is helpful to illustrate the effect of the auxiliary loads on the overall energy consumption.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-6-mobile is-6-tablet is-4-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/energyDistribution.png" alt="Example of Energy distributon">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="usedGenerated">Energy Used and Generated</h3>
          <p>The energy used and generated graph shows the consumed electrical energy by the compressor and the auxiliary units compared to the generated heating and cooling energy. On the electrical side there is a marking of the statistical signature consumption for this system over the selected quarter.</p>
          <p>If the system is made up of several units of different applications the chart will split heating and cooling per application-type.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/usedGenerated.png" alt="Example of used and generated energy">
              </figure>
            </div>
          </div>
          <div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/usedGenerated2.png" alt="Example of used and generated energy">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="usageHour">Energy Usage per Hour</h3>
          <p>The energy usage per hour shows in detail the actual and predicted energy consumption of the system as well as the ambient temperature over the report period. The chart shows the effect of the ambient temperature on the energy consumption and the hours where savings are achieved compared to the predicted energy or the hours with excessive energy consumption compared to the predicted energy.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/usageHour.png" alt="Example of used and generated energy">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="temperatureOccurrence">Ambient Temperature Occurence</h3>
          <p>The temperature occurrence shows the number of hours during which a certain ambient temperature has occurred. It is helpful to take this into account when comparing the energy consumption and performance of a system for two different periods or when comparing the performance of two systems in different locations.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-6-mobile is-6-tablet is-8-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/temperatureOccurrence.png" alt="Example of temperature occurrence graph">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="energyPerDegree">Energy per Degree Temperature</h3>
          <p>The energy consumption at different ambient temperature shows the consumed electrical and generated heating/cooling energy for each ambient temperature that occurred during the report period. The graph helps in localizing the temperatures where most energy is used and where the optimization should be focused on.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/energyPerDegree.png" alt="Example of energy per temperature graph">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="averageCop">Average COP during Report period</h3>
          <p>The average COP during the report period shows the cooling and heating COP.</p>
          <p>If the system is made up of several units of different applications the chart shows COP calculated per application-type.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-6-mobile is-6-tablet is-8-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/averageCop.png" alt="Example of average COP graph">
              </figure>
            </div>
          </div>
          <div class="columns ml-0">
            <div class="column box is-mobile is-6-mobile is-6-tablet is-8-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/averageCop2.png" alt="Example of average COP graph">
              </figure>
            </div>
          </div>
        </div>
        <hr>
        <div class="block content">
          <h3 class="title is-3" id="copPerDegree">COP per Degree Temperature</h3>
          <p>The COP at each occurring ambient temperature shows a more detailed report of the heating and cooling COP, averaged per degree temperature occurred during the report period.</p>
          <div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/copPerDegree.png" alt="Example of used COP per temperature graph">
              </figure>
            </div>
          </div><div class="columns ml-0">
            <div class="column box is-mobile is-8-mobile is-8-tablet is-10-desktop">
              <figure class="image">
                <img src="@/assets/manual_img/copPerDegree2.png" alt="Example of used COP per temperature graph">
              </figure>
            </div>
          </div>
        </div>
        <hr>
      </div>
    </section>
  </div>
</template>
<script>
export default {
  methods: {
    goToTitle: function (titleId) {
      const element = document.getElementById(titleId)
      if (element) {
        element.scrollIntoView()
      }
    }
  },
  computed: {
    system: function () {
      if (this.$store.state.system) {
        return this.$store.state.system
      } else {
        return null
      }
    },
    user: function () {
      const user = {
        name: null,
        password: null,
        authenticated: false,
        idSystem: null // the system this user is currently authenticated for.
      }

      if (localStorage.getItem('ccUsername') !== '') {
        user.name = localStorage.getItem('ccUsername')
      }

      if (localStorage.getItem('ccPassword') !== '') {
        user.password = localStorage.getItem('ccPassword')
      }

      user.idSystem = parseInt(localStorage.getItem('ccSystem'))
      user.authenticated = (localStorage.getItem('ccAuthenticated') === 'true')

      return user
    }
  },
  mounted () {
    // Check if user is authenticated for a system
    if (!this.user.authenticated) {
      // user need to login
      this.$router.push({ name: 'NotAllowed' })
    }
  }
}
</script>
